import { create } from "zustand";
import AnnouncementService, {
  Announcement
} from "../../../../services/AnnouncementService";

export type AnnouncementPopupSource = "login" | "bell";

type AnnouncementPopup = {
  open: boolean;
  source: AnnouncementPopupSource;
  /** Ids navegables con el contador "1 de N". */
  queue: number[];
  index: number;
};

type AnnouncementsState = {
  items: Announcement[];
  status: "idle" | "loading" | "loaded" | "error";
  userId?: string;
  popup: AnnouncementPopup;
  /**
   * Tildados con "No volver a mostrar" en esta sesión: salen de la campanita en el momento
   * pero siguen en `items` para que el popup abierto no desaparezca.
   */
  dismissedIds: number[];
  /** Vistas ya reportadas en esta sesión: reabrir no vuelve a contar (GDC-5186). */
  viewedIds: number[];
  load: (userId: string) => Promise<void>;
  openLoginPopup: () => void;
  openFromBell: (announcementId: number) => void;
  closePopup: () => void;
  goTo: (index: number) => void;
  markRead: (announcementId: number) => void;
  dismiss: (announcementId: number) => void;
  trackView: (announcementId: number) => void;
  trackClick: (announcementId: number) => void;
};

const closedPopup: AnnouncementPopup = {
  open: false,
  source: "login",
  queue: [],
  index: 0
};

const initialState = {
  items: [],
  status: "idle" as const,
  userId: undefined,
  popup: closedPopup,
  dismissedIds: [],
  viewedIds: []
};

export const useAnnouncementsStore = create<AnnouncementsState>((set, get) => ({
  ...initialState,

  load: async (userId) => {
    const { status, userId: loadedUserId } = get();
    if (
      loadedUserId === userId &&
      (status === "loading" || status === "loaded")
    ) {
      return;
    }
    set({ ...initialState, userId, status: "loading" });
    const response = await AnnouncementService.getList();
    if (get().userId !== userId) {
      return;
    }
    if (response instanceof Error) {
      set({ status: "error" });
      return;
    }
    set({ items: response.announcements ?? [], status: "loaded" });
  },

  openLoginPopup: () => {
    const queue = get()
      .items.filter((item) => !item.read)
      .map((item) => item.announcementId);
    if (queue.length === 0) {
      return;
    }
    set({ popup: { open: true, source: "login", queue, index: 0 } });
  },

  openFromBell: (announcementId) => {
    get().markRead(announcementId);
    set({
      popup: { open: true, source: "bell", queue: [announcementId], index: 0 }
    });
  },

  closePopup: () =>
    set((state) => ({ popup: { ...state.popup, open: false } })),

  goTo: (index) =>
    set((state) => {
      const total = state.popup.queue.length;
      if (total === 0) {
        return state;
      }
      return { popup: { ...state.popup, index: (index + total) % total } };
    }),

  markRead: (announcementId) => {
    const item = get().items.find((x) => x.announcementId === announcementId);
    if (!item || item.read) {
      return;
    }
    set((state) => ({
      items: state.items.map((x) =>
        x.announcementId === announcementId ? { ...x, read: true } : x
      )
    }));
    AnnouncementService.markRead(announcementId);
  },

  dismiss: (announcementId) => {
    if (get().dismissedIds.includes(announcementId)) {
      return;
    }
    set((state) => ({
      dismissedIds: [...state.dismissedIds, announcementId],
      items: state.items.map((x) =>
        x.announcementId === announcementId ? { ...x, read: true } : x
      )
    }));
    AnnouncementService.dismiss(announcementId);
  },

  trackView: (announcementId) => {
    if (get().viewedIds.includes(announcementId)) {
      return;
    }
    set((state) => ({ viewedIds: [...state.viewedIds, announcementId] }));
    AnnouncementService.track(announcementId, "view");
  },

  trackClick: (announcementId) => {
    AnnouncementService.track(announcementId, "click");
  }
}));

export const selectCurrentAnnouncement = (state: AnnouncementsState) => {
  const id = state.popup.queue[state.popup.index];
  return state.items.find((item) => item.announcementId === id);
};
